import type { ComponentPropsWithoutRef, ReactNode } from 'react';

import type { ButtonProps } from '@/components/base/Button';
import Button from '@/components/base/Button';
import type { H2Props } from '@/components/base/Headings/H2';
//Components
import H2 from '@/components/base/Headings/H2';
import Icon from '@/components/base/Icon';
import type { IllustrationSrcType } from '@/components/composite/Illustration';
import { Illustration } from '@/components/composite/Illustration';

type FeatureDescriptionProps = ComponentPropsWithoutRef<'div'>;

function FeatureDescription({ children }: FeatureDescriptionProps) {
    return (
        <div className="flex w-full flex-1 flex-col items-start gap-7 rounded-md bg-white p-15 dark:bg-icoDarkMode-anthracite lg:flex-row">
            {children}
        </div>
    );
}

/**
 * Left side
 */

type LeftSideProps = ComponentPropsWithoutRef<'div'>;

function LeftSide(props: LeftSideProps) {
    return <div className="flex-1" {...props} />;
}

FeatureDescription.LeftSide = LeftSide;

/**
 * Right side
 */

type RightSideProps = ComponentPropsWithoutRef<'div'>;

function RightSide(props: RightSideProps) {
    return <div className="w-full lg:max-w-xs xl:max-w-sm" {...props} />;
}

FeatureDescription.RightSide = RightSide;

/**
 * Title
 */

function Title(props: H2Props) {
    return <H2 className="text-icoBlack dark:text-white" {...props} />;
}

FeatureDescription.Title = Title;

/**
 * Description
 */
type DescriptionProps = ComponentPropsWithoutRef<'p'>;
function Description(props: DescriptionProps) {
    return <p className="mt-5 text-base text-icoGray-500 dark:text-icoDarkMode-wolf" {...props} />;
}

FeatureDescription.Description = Description;

/**
 * SubTitle
 */

export type SubTitleProps = ComponentPropsWithoutRef<'h3'>;
function SubTitle({ children, ...restProps }: SubTitleProps) {
    return (
        <h3 className="text-xl text-icoBlack dark:text-white" {...restProps}>
            {children}
        </h3>
    );
}

FeatureDescription.SubTitle = SubTitle;

/**
 * Option
 */

function Option({ children }: { children: string | ReactNode }) {
    return (
        <span className="flex items-start gap-3">
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-icoBlue-100 text-xs text-icoBlue dark:bg-icoBlue/20">
                <Icon icon="check" />
            </span>
            <p className="flex-1 pt-0.5 text-sm text-icoGray-500 dark:text-icoDarkMode-wolf">
                {children}
            </p>
        </span>
    );
}

FeatureDescription.Option = Option;

/**
 * Illustration
 */

function FeatureIllustration({ illustration }: { illustration: IllustrationSrcType }) {
    return (
        <div className="hidden lg:block">
            <Illustration src={{ en: illustration.en, fr: illustration.fr, es: illustration.es }} />
        </div>
    );
}

FeatureDescription.Illustration = FeatureIllustration;

/**
 * Button
 */

function FeatureButton(props: ButtonProps) {
    return <Button {...props} />;
}

FeatureDescription.Button = FeatureButton;

export default FeatureDescription;
